// Automations-Summary-Store: Liste der Regeln + neuester Log-Eintrag
// fuer die Dashboard-Karte (Headline "N Regeln aktiv", Subtitle
// "letzter Trigger vor X"). Beides kommt seit Mai 2026 aus dem Go-
// Stack (internal/api/automations.go + automations.Engine.Log()).
//
// Gelesen wird der Store heute nur von der Navigationssuche
// (lib/inventory-index.svelte.ts): start* holt EINMAL (die Namen für Suche
// und Embedding-Index), danach Takt 30 s nur solange die Suche offen ist
// (beobachteAutomations, lib/bei-bedarf.ts).

import { untrack } from "svelte";
import { SvelteMap } from "svelte/reactivity";
import { beiBedarf } from "./bei-bedarf";
import { registerRevalidator } from "./revalidate.svelte";

import { getJSON } from "./api";
import { loadPersisted, persist } from "./persistent-summary.svelte";
import type { Automation, AutomationLogEntry } from "./types";

export interface AutomationsSummary {
  rules: Automation[];
  lastTrigger: AutomationLogEntry | null;
}

const STORAGE_KEY = "automations";

const _summary = new SvelteMap<string, AutomationsSummary>();
let _started = false;

async function refresh(): Promise<void> {
  try {
    const [rules, log] = await Promise.all([
      getJSON<Automation[]>("/v2-api/automations"),
      getJSON<AutomationLogEntry[]>("/v2-api/automations/log"),
    ]);
    // Log ist aufsteigend sortiert (aelteste zuerst), neuester Eintrag
    // also am Ende.
    const last = log.length > 0 ? log[log.length - 1] : null;
    const next: AutomationsSummary = { rules, lastTrigger: last };
    _summary.set("current", next);
    persist(STORAGE_KEY, next);
  } catch {
    // ignore -- naechster Tick versucht's wieder
  }
}

// 30 s reicht: Triggers sind nicht zeitkritisch, die Detail-Page hat ihren
// eigenen 15-s-Loop.
// frisch: die Navigationssuche wird beim Tippen und Leeren der Palette neu gebaut
// (beobachteInventar) -- ein Abruf, der jünger als 30 s ist, genügt.
const _bedarf = beiBedarf({ hole: refresh, takt: 30_000, frisch: 30_000 });

export function startAutomationsSummary(): void {
  if (_started) return;
  _started = true;
  registerRevalidator(() => {
    if (_bedarf.aktiv()) void refresh();
  });
  const cached = loadPersisted<AutomationsSummary>(STORAGE_KEY);
  if (cached) _summary.set("current", cached);
  void refresh();
}

// Anmelden, solange die Suche die Regeln zeigt; der Rückgabewert meldet ab.
export function beobachteAutomations(): () => void {
  return untrack(() => {
    startAutomationsSummary();
    return _bedarf.beobachte();
  });
}

export function stopAutomationsSummary(): void {
  _started = false;
  _bedarf.beende();
  _summary.clear();
}

export function getAutomationsSummary(): AutomationsSummary | undefined {
  return _summary.get("current");
}
